import { JOB_IDS, type JobId } from './jobs.js';

/**
 * Task board model.
 *
 * Columns:  Backlog -> Ready -> In Progress -> Review -> Done
 * Blocked is a flag on a task, not a column, so a blocked task keeps its place
 * in the priority order instead of being exiled to a side lane.
 *
 * Priority is a single 1-100 number with tiered bands, so it is both trivially
 * sortable and readable at a glance.
 */

export const TASK_STATUSES = ['backlog', 'ready', 'in_progress', 'review', 'done'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const PRIORITY_MIN = 1;
export const PRIORITY_MAX = 100;

export type PriorityTier = 'low' | 'medium' | 'high' | 'immediate';

export interface PriorityBand {
  readonly tier: PriorityTier;
  readonly label: string;
  readonly min: number;
  readonly max: number;
}

export const PRIORITY_BANDS: readonly PriorityBand[] = [
  { tier: 'low', label: 'Low', min: 1, max: 25 },
  { tier: 'medium', label: 'Medium', min: 26, max: 50 },
  { tier: 'high', label: 'High', min: 51, max: 75 },
  { tier: 'immediate', label: 'Immediate', min: 76, max: 100 },
];

export function bandForPriority(priority: number): PriorityBand {
  const p = clampPriority(priority);
  for (const band of PRIORITY_BANDS) {
    if (p >= band.min && p <= band.max) return band;
  }
  // Unreachable after clamping, but keeps the return type total.
  return PRIORITY_BANDS[0]!;
}

export function clampPriority(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return PRIORITY_BANDS[1]!.min;
  return Math.min(PRIORITY_MAX, Math.max(PRIORITY_MIN, Math.round(n)));
}

export function isTaskStatus(value: unknown): value is TaskStatus {
  return typeof value === 'string' && (TASK_STATUSES as readonly string[]).includes(value);
}

/** Column order used for kanban rendering. */
export const STATUS_ORDER: readonly TaskStatus[] = TASK_STATUSES;

export interface Task {
  readonly id: number;
  readonly projectId: number;
  /** Unique, human-typeable handle. This is what you say to the Assistant. */
  readonly slug: string;
  readonly title: string;
  readonly description: string;
  readonly job: JobId;
  readonly priority: number;
  readonly status: TaskStatus;
  readonly assigneeId: number | null;
  readonly blocked: boolean;
  readonly blockedReason: string | null;
  readonly dependsOn: readonly number[];
  readonly createdBy: string;
  /** Member who last assigned this task. Null when a person or the scheduler did. */
  readonly assignedByMemberId: number | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly completedAt: string | null;
}

export interface CreateTaskInput {
  readonly projectId: number;
  readonly slug: string;
  readonly title: string;
  readonly description?: string;
  readonly job: JobId;
  readonly priority: number;
  readonly status?: TaskStatus;
  readonly assigneeId?: number | null;
  readonly blocked?: boolean;
  readonly blockedReason?: string | null;
  readonly dependsOn?: readonly number[];
  readonly createdBy: string;
}

/** Board columns for a set of tasks, ready for kanban rendering. */
export interface BoardSnapshot {
  readonly backlog: Task[];
  readonly ready: Task[];
  readonly inProgress: Task[];
  readonly review: Task[];
  readonly done: Task[];
  readonly blockedCount: number;
}

/**
 * Board ordering: priority descending, then oldest first, so equal-priority work
 * is consumed in the order it was filed.
 */
export function compareTasks(a: Task, b: Task): number {
  if (a.priority !== b.priority) return b.priority - a.priority;
  return a.createdAt.localeCompare(b.createdAt) || a.id - b.id;
}

export function groupIntoBoard(tasks: readonly Task[]): BoardSnapshot {
  const board: Record<TaskStatus, Task[]> = {
    backlog: [],
    ready: [],
    in_progress: [],
    review: [],
    done: [],
  };
  let blockedCount = 0;
  for (const task of tasks) {
    if (task.blocked) blockedCount += 1;
    board[task.status].push(task);
  }
  for (const status of STATUS_ORDER) board[status].sort(compareTasks);
  return {
    backlog: board.backlog,
    ready: board.ready,
    inProgress: board.in_progress,
    review: board.review,
    done: board.done,
    blockedCount,
  };
}

/* ------------------------------------------------------------------ *
 * Task names
 * ------------------------------------------------------------------ */

export const SLUG_MAX = 64;

export class SlugError extends Error {}

/** Normalise free text or speech into a kebab-case task name. */
export function slugify(input: string): string {
  const ascii = input
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['\u2019]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX)
    .replace(/-+$/g, '');
  return ascii;
}

export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) && slug.length <= SLUG_MAX;
}

export function assertValidSlug(slug: string): string {
  if (!slug) throw new SlugError('Task name is required.');
  if (!isValidSlug(slug)) {
    throw new SlugError(
      'Task names must be lowercase kebab-case (letters, digits and single dashes), e.g. "fix-login-redirect".',
    );
  }
  return slug;
}

/**
 * Resolve a spoken or typed reference to a task.
 *
 * Exact slug match wins outright. Otherwise normalise the input and compare
 * against slugs and titles, returning the scored candidates so the caller can
 * either take a confident hit or ask the user which one they meant.
 */
export interface NameMatch {
  readonly task: Task;
  readonly score: number;
}

export function resolveTaskName(tasks: readonly Task[], spoken: string): NameMatch[] {
  const needle = slugify(spoken);
  if (!needle) return [];

  const scored: NameMatch[] = [];
  for (const task of tasks) {
    let score = 0;
    if (task.slug === needle) score = 1;
    else if (slugify(task.title) === needle) score = 0.95;
    else if (task.slug.includes(needle)) score = 0.8;
    else if (needle.includes(task.slug)) score = 0.75;
    else if (slugify(task.title).includes(needle)) score = 0.6;
    else score = tokenOverlap(task.slug, needle);
    if (score > 0.34) scored.push({ task, score });
  }
  scored.sort((a, b) => b.score - a.score || compareTasks(a.task, b.task));
  return scored;
}

/**
 * Dice coefficient over hyphen-separated tokens.
 *
 * The naive Jaccard ratio badly under-scores the common case where someone
 * speaks a full sentence ("can you fix the login bug") against a short task name
 * ("fix-login-redirect"): the extra words drag the denominator up and the match
 * falls below any sane threshold. Averaging the two coverage directions fixes
 * that without making a one-word task name match every sentence.
 */
function tokenOverlap(slug: string, needle: string): number {
  const a = new Set(slug.split('-').filter(Boolean));
  const b = new Set(needle.split('-').filter(Boolean));
  if (a.size === 0 || b.size === 0) return 0;
  let hits = 0;
  for (const token of b) if (a.has(token)) hits += 1;
  return (hits / a.size + hits / b.size) / 2;
}

export function assertJobId(value: unknown): asserts value is JobId {
  if (typeof value !== 'string' || !(JOB_IDS as readonly string[]).includes(value)) {
    throw new SlugError(`Unknown job: ${String(value)}`);
  }
}
